"use client";

// src/button.tsx
import * as React2 from "react";
import {
  Base as Base2,
  systemForwardRef as systemForwardRef2,
  useStyleConfig as useStyleConfig2
} from "@stepstone-genesis/system";
import { Stack } from "@stepstone-genesis/components-stack";
import { Spinner } from "@stepstone-genesis/components-spinner";
import { isArray } from "@stepstone-genesis/utilities";

// src/button-base.tsx
import * as React from "react";
import {
  Base,
  systemForwardRef,
  useStyleConfig
} from "@stepstone-genesis/system";

// src/styles.ts
var disabledDefaultStyles = {
  color: "colorTextDisabledDefault",
  '[data-genesis-element="BUTTON_CONTENT"]': {
    color: "colorTextDisabledDefault"
  },
  cursor: "not-allowed",
  backgroundColor: "colorBackgroundDisabledDefault",
  boxShadow: "boxShadowNone",
  _focus: {
    color: "colorTextDisabledDefault",
    textDecoration: "none",
    backgroundColor: "colorBackgroundDisabledDefault"
  },
  _hover: {
    color: "colorTextDisabledDefault",
    backgroundColor: "colorBackgroundDisabledDefault"
  },
  _active: {
    color: "colorTextDisabledDefault",
    backgroundColor: "colorBackgroundDisabledDefault"
  }
};
var disabledLinkStyles = {
  ...disabledDefaultStyles,
  backgroundColor: "transparent",
  _hover: {
    color: "colorGreyLight",
    backgroundColor: "transparent",
    textDecoration: "none"
  },
  _active: {
    color: "colorGreyLight",
    backgroundColor: "transparent",
    textDecoration: "none"
  }
};
var disabledLinkSecondaryStyles = {
  ...disabledDefaultStyles,
  backgroundColor: "transparent",
  _hover: {
    backgroundColor: "transparent",
    textDecoration: "none"
  },
  _active: {
    backgroundColor: "transparent",
    textDecoration: "none"
  }
};
var root = {
  display: "inline-flex",
  appearance: "none",
  alignItems: "center",
  justifyContent: "center",
  transition: "background-color ease 0s",
  transitionDuration: "200ms",
  userSelect: "none",
  position: "relative",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  outline: "none",
  width: "auto",
  fontFamily: "fontFamilyBody",
  minWidth: "size50",
  color: "colorWhite",
  padding: "spaceNone",
  border: "none",
  cursor: "pointer",
  fontWeight: "fontWeightNormal",
  textDecoration: "none",
  backgroundColor: "transparent",
  _disabled: {
    ...disabledDefaultStyles
  },
  _focusVisible: {
    outline: "outlineDefault",
    outlineOffset: "2px",
    boxShadow: "boxShadowFocusWhite"
  }
};
var getPaddingX = (isIconOnly, isIconBefore, isIconAfter, size, padding) => {
  if (isIconOnly) {
    return { px: "spaceNone" };
  }
  const customSidePadding = size === "large" ? "16px" : "12px";
  return {
    pl: isIconBefore ? customSidePadding : padding,
    pr: isIconAfter ? customSidePadding : padding
  };
};
var sizes = (isIconOnly, isIconBefore, isIconAfter) => ({
  button: {
    small: {
      fontSize: "fontSizeS",
      lineHeight: "lineHeightS",
      minHeight: "size40",
      minWidth: "size40",
      borderRadius: "componentButtonSmallRadius",
      fontWeight: "fontWeightBold",
      ...getPaddingX(isIconOnly, isIconBefore, isIconAfter, "small", "spaceM")
    },
    medium: {
      fontSize: "fontSizeM",
      lineHeight: "lineHeightM",
      minHeight: "componentButtonMediumSize",
      minWidth: "componentButtonMediumSize",
      borderRadius: "componentButtonMediumRadius",
      fontWeight: "fontWeightBold",
      ...getPaddingX(isIconOnly, isIconBefore, isIconAfter, "medium", "spaceM")
    },
    large: {
      fontSize: "fontSizeL",
      lineHeight: "lineHeightL",
      minHeight: "size70",
      minWidth: "size70",
      fontWeight: "componentButtonLargeFontWeight",
      borderRadius: "componentButtonLargeRadius",
      ...getPaddingX(
        isIconOnly,
        isIconBefore,
        isIconAfter,
        "large",
        "componentButtonLargePaddingX"
      )
    }
  },
  icons: {
    small: {
      size: "small"
    },
    medium: {
      size: "medium"
    },
    large: {
      size: "large"
    }
  }
});
var variants = {
  primary: {
    color: "colorWhite",
    backgroundColor: "colorPrimaryDefault",
    boxShadow: "componentButtonPrimaryBoxShadow",
    _hover: {
      backgroundColor: "colorPrimaryHover",
      boxShadow: "boxShadowNone"
    },
    _active: {
      backgroundColor: "colorPrimaryActive",
      boxShadow: "boxShadowNone",
      _hover: {
        backgroundColor: "colorPrimaryActive"
      }
    },
    _disabled: {
      ...disabledDefaultStyles
    }
  },
  secondary: {
    color: "componentButtonSecondaryBorderColorDefault",
    backgroundColor: "componentButtonSecondaryBgColorDefault",
    boxShadow: "componentButtonSecondaryBoxShadow",
    '[data-genesis-element="BUTTON_CONTENT"]': {
      color: "componentButtonSecondaryTextColorDefault"
    },
    _hover: {
      color: "componentButtonSecondaryBorderColorHover",
      backgroundColor: "componentButtonSecondaryBgColorHover",
      boxShadow: "boxShadowBorderDefault"
    },
    _active: {
      color: "componentButtonSecondaryBorderColorActive",
      backgroundColor: "componentButtonSecondaryBgColorActive",
      boxShadow: "boxShadowBorderDefault",
      _hover: {
        color: "componentButtonSecondaryBorderColorHover"
      }
    },
    _disabled: {
      ...disabledDefaultStyles
    }
  },
  tertiary: {
    color: "componentButtonTertiaryBorderColorDefault",
    backgroundColor: "componentButtonTertiaryBgColorDefault",
    boxShadow: "componentButtonTertiaryBoxShadow",
    '[data-genesis-element="BUTTON_CONTENT"]': {
      color: "componentButtonTertiaryTextColorDefault"
    },
    _hover: {
      color: "componentButtonTertiaryBorderColorHover",
      backgroundColor: "componentButtonTertiaryBgColorHover",
      boxShadow: "boxShadowNone"
    },
    _active: {
      color: "componentButtonTertiaryBorderColorActive",
      backgroundColor: "componentButtonTertiaryBgColorActive",
      boxShadow: "boxShadowNone",
      _hover: {
        color: "componentButtonTertiaryBorderColorHover"
      }
    },
    _disabled: {
      ...disabledDefaultStyles
    }
  },
  accentB2B: {
    color: "colorWhite",
    backgroundColor: "colorAccentB2BDefault",
    boxShadow: "boxShadowNone",
    _hover: {
      backgroundColor: "colorAccentB2BHover"
    },
    _active: {
      backgroundColor: "colorAccentB2BActive",
      _hover: {
        backgroundColor: "colorAccentB2BActive"
      }
    }
  },
  accentB2C: {
    color: "colorWhite",
    backgroundColor: "colorAccentB2CDefault",
    boxShadow: "boxShadowNone",
    _hover: {
      backgroundColor: "colorAccentB2CHover"
    },
    _active: {
      backgroundColor: "colorAccentB2CActive",
      _hover: {
        backgroundColor: "colorAccentB2CActive"
      }
    }
  },
  link: {
    color: "componentButtonLinkTextColorDefault",
    backgroundColor: "transparent",
    boxShadow: "boxShadowNone",
    paddingX: "spaceNone",
    _hover: {
      color: "componentButtonLinkTextColorHover",
      backgroundColor: "transparent",
      textDecoration: "componentButtonLinkTextDecoration"
    },
    _active: {
      color: "componentButtonLinkTextColorActive",
      backgroundColor: "transparent",
      textDecoration: "componentButtonLinkTextDecoration",
      _hover: {
        color: "componentButtonLinkTextColorActive"
      }
    },
    _disabled: {
      ...disabledLinkStyles
    }
  },
  linkSecondary: {
    color: "colorGreyDark",
    backgroundColor: "transparent",
    boxShadow: "boxShadowNone",
    paddingX: "spaceNone",
    _hover: {
      color: "colorGreyDarker",
      backgroundColor: "transparent",
      textDecoration: "underline"
    },
    _active: {
      color: "colorBlack",
      backgroundColor: "transparent",
      textDecoration: "underline",
      _focus: {
        color: "colorGreyDarker"
      }
    },
    _disabled: {
      ...disabledLinkSecondaryStyles
    }
  },
  disruptive: {
    color: "colorWhite",
    backgroundColor: "colorStatusNegativeDefault",
    boxShadow: "boxShadowNone",
    _hover: {
      backgroundColor: "colorStatusNegativeHover"
    },
    _active: {
      backgroundColor: "colorStatusNegativeActive",
      _hover: {
        backgroundColor: "colorStatusNegativeActive"
      }
    }
  },
  disruptiveSecondary: {
    color: "colorBlack",
    backgroundColor: "colorStatusNegativeLight",
    boxShadow: "boxShadowNone",
    _hover: {
      backgroundColor: "colorStatusNegativeLightHover"
    },
    _active: {
      backgroundColor: "colorStatusNegativeLightActive",
      _hover: {
        backgroundColor: "colorStatusNegativeLightActive"
      }
    }
  },
  inverted: {
    backgroundColor: "componentButtonInvertedBgColorDefault",
    color: "componentButtonInvertedBorderColorDefault",
    '[data-genesis-element="BUTTON_CONTENT"]': {
      color: "componentButtonInvertedTextColorDefault"
    },
    boxShadow: "boxShadowNone",
    _hover: {
      '[data-genesis-element="BUTTON_CONTENT"]': {
        color: "componentButtonInvertedTextColorHover"
      }
    },
    _active: {
      backgroundColor: "componentButtonInvertedBgColorActive",
      '[data-genesis-element="BUTTON_CONTENT"]': {
        color: "componentButtonInvertedTextColorActive"
      },
      _hover: {
        color: "componentButtonInvertedBorderColorHover"
      }
    },
    _disabled: {
      '[data-genesis-element="BUTTON_CONTENT"]': {
        color: "componentButtonInvertedDisabledTextColor"
      },
      backgroundColor: "componentButtonInvertedDisabledBgColor",
      _hover: {
        color: "componentButtonInvertedDisabledTextColor",
        backgroundColor: "componentButtonInvertedDisabledBgColor",
        textDecoration: "none"
      },
      _active: {
        color: "componentButtonInvertedDisabledTextColor",
        backgroundColor: "componentButtonInvertedDisabledBgColor",
        textDecoration: "none"
      }
    }
  }
};
var styles = {
  root,
  sizes,
  variants
};

// src/utils.ts
var checkForButtonErrors = ({
  as,
  href,
  isDisabled,
  isLoading,
  tabIndex,
  iconOnly,
  iconAfter,
  iconBefore
}) => {
  const validators = {
    HREF_WITHOUT_A_TAG: {
      condition: as !== "a" && Boolean(href),
      message: `[Genesis: Button] You cannot pass a href into a button without the 'a' tag.  Please make sure to use 'as="a".`
    },
    A_TAG_WITHOUT_HREF: {
      condition: as === "a" && !href,
      message: `[Genesis: Button] When adding 'as="a"' you must specify a 'href' tag.`
    },
    A_TAG_AND_DISABLED_OR_LOADING: {
      condition: as === "a" && Boolean(isDisabled || isLoading),
      message: `[Genesis: Button] <Button as="a"> cannot have a state of disabled or loading.`
    },
    TAB_INDEX_OUT_OF_RANGE: {
      condition: tabIndex != null && !(tabIndex === 0 || tabIndex === -1),
      message: `[Genesis: Button] TabIndex should only be either 0 or -1`
    },
    ICON_ONLY: {
      condition: Boolean(iconOnly) && Boolean(iconAfter || iconBefore),
      message: `[Genesis: Button] When using iconOnly all other icon props should not be provided (iconAfter, iconBefore). Children is also forbidden.`
    }
  };
  const validate = Object.keys(validators).map((key) => {
    try {
      if (validators[key]["condition"]) {
        throw new Error(validators[key]["message"]);
      }
    } catch (error) {
      console.error(error);
    }
  });
  return validate;
};

// src/button-base.tsx
var ButtonBase = systemForwardRef(
  function ButtonBase2(props, ref) {
    const {
      children,
      a11yLabel,
      isLoading = false,
      isDisabled = false,
      type = "button",
      href,
      as = "button",
      tabIndex,
      externalStyles = [],
      role,
      ...rest
    } = props;
    const { BUTTON_BASE } = useStyleConfig("BUTTON_BASE", {
      styles: {
        base: [styles.root, ...externalStyles]
      }
    });
    React.useMemo(
      () => checkForButtonErrors({
        as,
        isDisabled,
        isLoading,
        href,
        tabIndex
      }),
      [as, isDisabled, isLoading, href, tabIndex]
    );
    const validA11yLabel = a11yLabel !== "" ? a11yLabel : void 0;
    return /* @__PURE__ */ React.createElement(
      Base,
      {
        as,
        ref,
        role,
        "aria-label": validA11yLabel,
        __themeDefinition: "buttons",
        element: "BUTTON_BASE",
        __root: BUTTON_BASE.styles,
        href,
        disabled: isDisabled || isLoading,
        "aria-busy": isLoading,
        type: as === "a" ? void 0 : type,
        tabIndex,
        ...rest
      },
      children
    );
  }
);

// src/button.tsx
var formatIconsSizes = (sizes2, isIconOnly, isIconBefore, isIconAfter) => {
  if (!sizes2)
    return {};
  const formattedDirection = isArray(sizes2) ? sizes2.map(
    (size) => size && styles.sizes(isIconOnly, isIconBefore, isIconAfter).icons[size].size
  ) : styles.sizes(isIconOnly, isIconBefore, isIconAfter).icons[sizes2].size;
  return formattedDirection;
};
var ButtonContent = ({
  children,
  iconBefore: IconBefore,
  iconAfter: IconAfter,
  iconOnly: IconOnly,
  size,
  isLoading,
  loadingText
}) => {
  const isIconOnly = IconOnly != void 0;
  const formattedIconSizes = React2.useMemo(() => {
    return {
      size: formatIconsSizes(size, isIconOnly, !!IconBefore, !!IconAfter)
    };
  }, [isIconOnly, size]);
  return /* @__PURE__ */ React2.createElement(
    Stack,
    {
      as: "span",
      direction: "horizontal",
      spacing: IconOnly ? "spaceNone" : "spaceS",
      flxItems: "center",
      flxShrink: 0,
      wrapChildElement: false,
      element: "BUTTON_CONTENT"
    },
    IconBefore ? React2.cloneElement(IconBefore, {
      ...size && formattedIconSizes,
      ...IconBefore.props
    }) : null,
    /* @__PURE__ */ React2.createElement(Base2, { as: "span" }, isLoading && loadingText ? loadingText : children),
    IconAfter && !isLoading ? React2.cloneElement(IconAfter, {
      ...size && formattedIconSizes,
      ...IconAfter.props
    }) : null
  );
};
var Button = systemForwardRef2(function Button2(props, ref) {
  var _a;
  const {
    children,
    a11yLabel = "change me",
    variant = "primary",
    size = "medium",
    isFullWidth = false,
    iconBefore: IconBefore = void 0,
    iconAfter: IconAfter = void 0,
    iconOnly: IconOnly = void 0,
    loadingIcon = /* @__PURE__ */ React2.createElement(Spinner, { as: "span", sx: { display: "block" }, size: "small" }),
    isLoading = false,
    loadingText,
    isDisabled = false,
    type = "button",
    href,
    as = "button",
    tabIndex,
    onClick,
    buttonExternalStyles,
    ...rest
  } = props;
  const external = buttonExternalStyles != null ? buttonExternalStyles : {};
  const overrides = {
    ...isLoading ? { _disabled: { cursor: "wait" } } : {},
    ...isFullWidth ? { width: "100%" } : {}
  };
  const { BUTTON } = useStyleConfig2("BUTTON", {
    // This was the merge order from before - maybe we change this later so we do not need to define it.
    mergeOrder: ["base", "responsiveVariants", "variants"],
    styles: {
      base: {},
      variants: styles.variants,
      responsiveVariants: [
        {
          id: "button-size",
          prop: size,
          variants: styles.sizes(
            IconOnly != void 0,
            !!IconBefore,
            !!IconAfter
          ).button
        }
      ]
    },
    props: {
      variant,
      size
    }
  });
  const base = BUTTON.styles;
  const buttonStyles = [base, external, overrides];
  React2.useMemo(
    () => checkForButtonErrors({
      as,
      isDisabled,
      isLoading,
      href,
      tabIndex,
      iconOnly: IconOnly,
      iconBefore: IconBefore,
      iconAfter: IconAfter
    }),
    [as, isDisabled, isLoading, href, tabIndex, IconOnly, IconBefore, IconAfter]
  );
  const getIcon = (icon) => {
    if (typeof icon === "string" || typeof icon === "number") {
      const initials = icon.toString().slice(0, 3).toUpperCase();
      return /* @__PURE__ */ React2.createElement(React2.Fragment, null, initials);
    }
    return icon;
  };
  return /* @__PURE__ */ React2.createElement(
    ButtonBase,
    {
      as,
      ref,
      variant,
      a11yLabel,
      externalStyles: buttonStyles,
      href,
      isDisabled,
      isLoading,
      type,
      tabIndex,
      onClick,
      element: "BUTTON",
      ...rest
    },
    /* @__PURE__ */ React2.createElement(
      ButtonContent,
      {
        iconBefore: isLoading ? loadingIcon : (_a = getIcon(IconOnly)) != null ? _a : IconBefore,
        iconOnly: IconOnly,
        iconAfter: IconAfter,
        size,
        isLoading,
        loadingText
      },
      children
    )
  );
});
export {
  Button,
  ButtonBase,
  styles as buttonStyles
};
